import React from "react";
import PropTypes from "prop-types";
import { useLanguage } from "../contexts/LanguageContext";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "terriary" | "danger" | "remove";
  size?: "small" | "medium" | "large";
  isSaving?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  iconPosition?: "left" | "right";
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = "primary",
  size = "medium",
  isSaving = false,
  icon: Icon,
  iconPosition = "left",
  className = "",
  disabled,
  ...props
}) => {
  const { t } = useLanguage();
  const baseStyles = "rounded-md font-medium transition-colors duration-200";

  const variantStyles = {
    primary:
      "bg-indigo-600 border border-transparent text-white hover:bg-indigo-700",
    secondary:
      "bg-white border border-gray-300 text-gray-900 hover:bg-gray-100",
    terriary:
      "bg-gray-600 border border-transparent text-white hover:bg-gray-700",
    danger: "border border-transparent bg-red-600 text-white hover:bg-red-700",
    remove: "border border-transparent text-red-600 hover:bg-red-100",
  };

  const sizeStyles = {
    small: "px-3 py-1.5 text-sm",
    medium: "px-4 py-2 text-sm",
    large: "px-5 py-2.5 text-base",
  };

  const renderContent = () => {
    if (isSaving) {
      return (
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-b-transparent border-white"></div>
          {t("common.saving")}
        </div>
      );
    }

    if (!Icon) {
      return children;
    }

    const iconElement = <Icon className="w-5 h-5" />;

    return (
      <div className="flex items-center gap-2">
        {iconPosition === "left" && iconElement}
        {children}
        {iconPosition === "right" && iconElement}
      </div>
    );
  };

  return (
    <button
      className={`
                ${baseStyles}
                ${variantStyles[variant]}
                ${sizeStyles[size]}
                ${isSaving && !disabled ? "opacity-70 cursor-not-allowed" : ""}
                ${disabled ? "opacity-50 cursor-not-allowed" : ""}
                ${className}
                `}
      disabled={disabled || isSaving}
      {...props}
    >
      {renderContent()}
    </button>
  );
};

export default Button;
